const DEFAULT_API_URL = "https://api.baluu.io";

export type CreateSessionResult = {
  sessionId: string;
  schoolName: string;
  welcomeMessage: string;
};

export type SendMessageResult = {
  response: string;
  toolsUsed: string[];
};

function resolveApiUrl(apiUrl?: string): string {
  if (apiUrl) return apiUrl.replace(/\/$/, "");
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
    return String(import.meta.env.VITE_API_URL).replace(/\/$/, "");
  }
  return DEFAULT_API_URL;
}

/**
 * Creates an anonymous web-chat session for the school's widget.
 */
export async function createChatSession(
  schoolSlug: string,
  apiUrl?: string
): Promise<CreateSessionResult> {
  const base = resolveApiUrl(apiUrl);
  const res = await fetch(`${base}/v1/chat/${encodeURIComponent(schoolSlug)}/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}"
  });
  if (!res.ok) {
    throw new Error(`Failed to start chat session (${res.status})`);
  }
  const json = await res.json();
  return {
    sessionId: json.sessionId,
    schoolName: json.schoolName,
    welcomeMessage: json.welcomeMessage
  };
}

/**
 * Streams the agent's reply token-by-token over SSE (POST → fetch reader).
 * Calls `onDelta` for each text chunk; resolves when the stream completes.
 */
export async function streamChatMessage(
  schoolSlug: string,
  sessionId: string,
  message: string,
  apiUrl: string | undefined,
  onDelta: (text: string) => void,
  authToken?: string
): Promise<void> {
  const base = resolveApiUrl(apiUrl);
  const res = await fetch(`${base}/v1/chat/${encodeURIComponent(schoolSlug)}/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream"
    },
    body: JSON.stringify({ sessionId, message, authToken })
  });
  if (!res.ok || !res.body) {
    throw new Error(`Failed to stream message (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let errored = false;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const evt of events) {
      const line = evt.trim();
      if (!line.startsWith("data:")) continue;
      const json = line.slice(5).trim();
      if (!json) continue;
      try {
        const parsed = JSON.parse(json) as { type: string; text?: string };
        if (parsed.type === "delta" && parsed.text) onDelta(parsed.text);
        else if (parsed.type === "error") errored = true;
      } catch {
        // ignore malformed chunk
      }
    }
  }

  if (errored) throw new Error("stream error");
}

/**
 * Sends a customer message and returns the agent's reply (non-streaming
 * fallback). Calls the SAME `runAgent` brain the WhatsApp channel uses.
 */
export async function sendChatMessage(
  schoolSlug: string,
  sessionId: string,
  message: string,
  apiUrl?: string,
  authToken?: string
): Promise<SendMessageResult> {
  const base = resolveApiUrl(apiUrl);
  const res = await fetch(`${base}/v1/chat/${encodeURIComponent(schoolSlug)}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, message, authToken })
  });
  if (!res.ok) {
    throw new Error(`Failed to send message (${res.status})`);
  }
  const json = await res.json();
  return {
    response: json.response ?? "",
    toolsUsed: json.toolsUsed ?? []
  };
}

export type ChatRenderConfig = { render: boolean };

/**
 * Public render decision for the bubble — the API resolves the school's
 * display toggle + hide-when-no-credits server-side and returns a bare
 * boolean (no credit data reaches the browser).
 */
export async function fetchChatConfig(
  schoolSlug: string,
  channel: "website" | "webcomponent",
  apiUrl?: string,
  signal?: AbortSignal
): Promise<ChatRenderConfig> {
  const base = resolveApiUrl(apiUrl);
  const res = await fetch(
    `${base}/v1/chat/${encodeURIComponent(schoolSlug)}/config?channel=${channel}`,
    { signal }
  );
  if (!res.ok) {
    throw new Error(`Failed to load chat config (${res.status})`);
  }
  const json = await res.json();
  return { render: json.render !== false };
}
